import React, { useContext, useMemo } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Flex,
  Row,
  Segmented,
  Space,
  Statistic,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import NozomiReconciliationContext from '@totem/components/administration/reconciliation/nozomi/NozomiReconciliationContext';
import NozomiReconciliationTable from '@totem/components/administration/reconciliation/nozomi/NozomiReconciliationTable';
import {
  CoverageFilter,
  ListFilter,
  RowFilter,
} from '@totem/components/administration/reconciliation/nozomi/types';
import {
  buildRows,
  getTotals,
  hasNoAssets,
  isNotMapped,
  isNotSynced,
} from '@totem/components/administration/reconciliation/nozomi/utilities';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';

const formatTime = (value?: string) =>
  value ? dayjs(value).format('MMM D, YYYY h:mm A') : '';

const EXPORT_HEADERS = [
  'Organization',
  'Sensor',
  'Building',
  'Status',
  'Assets',
  'Matched',
  'Created',
  'Not Under Contract',
  'No Unique MAC',
  'No Contract (New)',
  'Multiple Matches',
  'No Building',
  'Create Failed',
  'Creation Disabled',
  'Portal Synced',
  'Portal Active',
  'Not Seen',
  'Asset Keys',
  'Merged',
  'Max Assets per Device',
  'Variance',
  'Flags',
];

export const NozomiReconciliationExtra = () => {
  const { data, loading, onRefresh } = useContext(NozomiReconciliationContext);

  const exportRows = useMemo(
    () =>
      (data?.sites ?? []).map((site) => [
        site.organization.name,
        site.siteName,
        site.building?.name ?? '',
        site.status,
        site.assets,
        site.matched,
        site.created,
        site.notUnderContract,
        site.noUniqueMac,
        site.notUnderContractNew,
        site.multipleMatches,
        site.noBuilding,
        site.createFailed,
        site.creationDisabled,
        site.portalSynced,
        site.portalActive,
        site.notSeen,
        site.assetKeys,
        site.merged,
        site.maxAssetsPerDevice,
        site.variance,
        [
          site.duplicateSensor ? 'Duplicate sensor' : '',
          site.mapped ? '' : 'Not mapped',
        ]
          .filter(Boolean)
          .join('; '),
      ]),
    [data],
  );

  return (
    <Space>
      {data?.hasRun && (
        <Typography.Text type="secondary">
          Sync run {formatTime(data.runStart)} –{' '}
          {dayjs(data.runEnd).format('h:mm A')}
        </Typography.Text>
      )}
      <IBTableExportButton
        fileName="nozomi-reconciliation"
        headers={EXPORT_HEADERS}
        rows={exportRows}
      />
      <Tooltip title="Refresh">
        <Button
          icon={<ReloadOutlined />}
          aria-label="Refresh"
          onClick={onRefresh}
          loading={loading}
        />
      </Tooltip>
    </Space>
  );
};

type SummaryTileProps = {
  title: string;
  value: number;
  hint: string;
  valueColor?: string;
  active?: boolean;
  onClick?: () => void;
};

const SummaryTile = ({
  title,
  value,
  hint,
  valueColor,
  active,
  onClick,
}: SummaryTileProps) => {
  const { token } = theme.useToken();

  const card = (
    <Card
      size="small"
      hoverable={!!onClick}
      style={{
        height: '100%',
        borderColor: active ? token.colorPrimary : undefined,
        boxShadow: active ? `0 0 0 1px ${token.colorPrimary}` : undefined,
      }}
    >
      <Statistic
        title={title}
        value={value}
        valueStyle={valueColor ? { color: valueColor } : undefined}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {hint}
      </Typography.Text>
    </Card>
  );

  if (!onClick) {
    return card;
  }

  return (
    <Tooltip
      title={active ? 'Show all sensors with assets' : 'Show these sensors'}
    >
      <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'none',
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        {card}
      </button>
    </Tooltip>
  );
};

const CoverageSummary = ({
  onCoverageFilter,
}: {
  onCoverageFilter: (filter: CoverageFilter) => void;
}) => {
  const { token } = theme.useToken();
  const { data, filter } = useContext(NozomiReconciliationContext);
  const sites = data?.sites ?? [];

  const mapped = sites.filter((site) => site.mapped).length;
  const notMapped = sites.filter(isNotMapped).length;
  const notSynced = data?.hasRun ? sites.filter(isNotSynced).length : 0;
  const noAssets = sites.filter(hasNoAssets).length;

  const tiles: (SummaryTileProps & { key: string })[] = [
    {
      key: 'mapped',
      title: 'Sensors Mapped',
      value: mapped,
      hint: `${sites.length} sensor records, ${sites.filter((site) => site.status === 'Inactive').length} inactive`,
    },
    {
      key: 'notMapped',
      title: 'Sensors Not Mapped',
      value: notMapped,
      hint: 'No building assigned, so the sync skips them',
      valueColor: notMapped > 0 ? token.colorWarning : undefined,
      active: filter === 'notMapped',
      onClick: () => onCoverageFilter('notMapped'),
    },
    {
      key: 'notSynced',
      title: 'Sensors Not Synced',
      value: notSynced,
      hint: 'Mapped, but no results in the latest run',
      valueColor: notSynced > 0 ? token.colorError : undefined,
      active: filter === 'notSynced',
      onClick: () => onCoverageFilter('notSynced'),
    },
    {
      key: 'noAssets',
      title: 'Sensors With No Assets',
      value: noAssets,
      hint: 'Synced, but Vantage returned nothing. The Guardian may be offline.',
      valueColor: noAssets > 0 ? token.colorWarning : undefined,
      active: filter === 'noAssets',
      onClick: () => onCoverageFilter('noAssets'),
    },
  ];

  return (
    <Row gutter={[12, 12]}>
      {tiles.map(({ key, ...tile }) => (
        <Col key={key} xs={24} sm={12} xl={6}>
          <SummaryTile {...tile} />
        </Col>
      ))}
    </Row>
  );
};

const AssetSummary = () => {
  const { token } = theme.useToken();
  const { data } = useContext(NozomiReconciliationContext);
  const sites = data?.sites ?? [];
  const totals = getTotals(sites);
  const skipped =
    totals.noUniqueMac +
    totals.notUnderContractNew +
    totals.multipleMatches +
    totals.noBuilding +
    totals.otherSkipped;
  const sensorsWithAssets = sites.filter((site) => site.assets > 0).length;

  const tiles = [
    {
      title: 'Vantage Assets',
      value: totals.assets,
      hint: `Returned for ${sensorsWithAssets} sensors this run`,
    },
    {
      title: 'Skipped',
      value: skipped,
      hint: `${totals.noUniqueMac.toLocaleString()} have no unique MAC`,
    },
    {
      title: 'Portal Devices Synced',
      value: totals.portalSynced,
      hint: 'Active devices refreshed this run',
    },
    {
      title: 'Assets Merged',
      value: totals.merged,
      hint: `${totals.multiAssetDevices.toLocaleString()} devices hold more than one asset`,
    },
    {
      title: 'Not Seen This Run',
      value: totals.notSeen,
      hint: 'Active devices not refreshed',
    },
    {
      title: 'Unexplained Variance',
      value: totals.variance,
      hint: 'Assets − skipped − synced − merged',
      valueColor: totals.variance === 0 ? token.colorSuccess : token.colorError,
    },
  ];

  return (
    <Row gutter={[12, 12]}>
      {tiles.map((tile) => (
        <Col key={tile.title} xs={24} sm={12} md={8} xl={4}>
          <SummaryTile {...tile} />
        </Col>
      ))}
    </Row>
  );
};

const COVERAGE_FILTER_LABELS: Record<CoverageFilter, string> = {
  notMapped: 'Sensors not mapped',
  notSynced: 'Sensors not synced',
  noAssets: 'Sensors with no assets',
};

const isCoverageFilter = (filter: RowFilter): filter is CoverageFilter =>
  filter in COVERAGE_FILTER_LABELS;

const Definitions = () => (
  <Collapse
    ghost
    items={[
      {
        key: 'definitions',
        label: 'How to read this report',
        children: (
          <Flex vertical gap={12}>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              Each row reconciles as{' '}
              <Typography.Text code>
                Assets − Skipped = Synced + Merged ± Variance
              </Typography.Text>
              . A variance of 0 means every asset Vantage returned is either on
              a portal device or skipped for a known reason.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              Assets is what Vantage returned for the sensor in the latest run:
              assets with activity since the previous sync, not the sensor’s
              full inventory. When comparing with Vantage, filter its asset list
              by last-seen time. Key sync dates only keep the latest run, so the
              report always shows the latest run.
            </Typography.Paragraph>
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                {
                  key: 'merged',
                  label: 'Assets per device',
                  children:
                    'Nozomi often creates one asset per network interface, and every asset behind a Tempered Airwall shares the gateway’s MAC, so several assets can land on one portal device.',
                },
                {
                  key: 'noMac',
                  label: 'No unique MAC',
                  children:
                    'Every MAC on the asset is locally administered or randomized, so there is nothing safe to match on.',
                },
                {
                  key: 'contract',
                  label: 'Not under contract / No contract',
                  children:
                    'The building has no active Threat Detection or Asset Inventory line. Existing devices stay linked but are not updated; new assets are not created.',
                },
                {
                  key: 'multiple',
                  label: 'Multiple matches',
                  children:
                    'The asset’s keys match more than one portal device. Merge the duplicates.',
                },
                {
                  key: 'building',
                  label: 'No building',
                  children:
                    'Neither the sensor nor the asset’s zone mapping assigns a building.',
                },
                {
                  key: 'duplicate',
                  label: 'Duplicate sensor',
                  children:
                    'Two mapped sensor records share a sensor name, usually after a Guardian is re-registered in Vantage. Both pull the same assets; set the older record to Inactive.',
                },
              ]}
            />
          </Flex>
        ),
      },
    ]}
  />
);

export const NozomiReconciliationLayout = () => {
  const { data, loading, filter, onFilterChange, onExpandedKeysChange } =
    useContext(NozomiReconciliationContext);

  const organizationKeys = useMemo(
    () => buildRows(data?.sites ?? [], filter).map((row) => row.key),
    [data, filter],
  );

  // Coverage tiles toggle their filter and open every organization that has a matching sensor,
  // since the matching rows are the point of clicking the tile.
  const onCoverageFilter = (coverageFilter: CoverageFilter) => {
    const nextFilter: RowFilter =
      filter === coverageFilter ? 'withAssets' : coverageFilter;
    onFilterChange(nextFilter);

    onExpandedKeysChange(
      nextFilter === coverageFilter
        ? buildRows(data?.sites ?? [], nextFilter).map((row) => row.key)
        : [],
    );
  };

  return (
    <Flex vertical gap={16}>
      {!loading && data && !data.hasRun && (
        <Alert
          type="warning"
          showIcon
          message="No Nozomi asset sync has run in the last 14 days."
        />
      )}
      <CoverageSummary onCoverageFilter={onCoverageFilter} />
      <AssetSummary />
      <Flex justify="space-between" align="center" gap={12} wrap>
        <Flex align="center" gap={8} wrap>
          <Segmented<string>
            value={isCoverageFilter(filter) ? '' : filter}
            onChange={(value) => onFilterChange(value as ListFilter)}
            options={[
              { label: 'All Sensors', value: 'all' },
              { label: 'With Assets', value: 'withAssets' },
              { label: 'Needs Attention', value: 'needsAttention' },
            ]}
          />
          {isCoverageFilter(filter) && (
            <Tag
              closable
              color="processing"
              onClose={() => onFilterChange('withAssets')}
            >
              {COVERAGE_FILTER_LABELS[filter]}
            </Tag>
          )}
        </Flex>
        <Space>
          <Button onClick={() => onExpandedKeysChange(organizationKeys)}>
            Expand All
          </Button>
          <Button onClick={() => onExpandedKeysChange([])}>Collapse All</Button>
        </Space>
      </Flex>
      <NozomiReconciliationTable />
      <Definitions />
    </Flex>
  );
};

export default NozomiReconciliationLayout;
